import styled from 'styled-components';

/* Shared box-score theme for every ag-grid table on the Game Overview page.
   Maps kitbag tokens onto AG Grid v34's CSS variables so all grids — the
   Play by Play Drives grid plus the StatGrid-based Team/Player/Scoring/Score
   tables — read identically. Match the kitbag Table tokens used in the
   Scoring Plays table:
   - Header cells: typography.headingSmall + ink.primary.main @ secondary opacity
   - Data cells:   typography.bodyMedium  + ink.primary.main */
export const AgGridThemedWrap = styled.div`
  width: 100%;
  height: 100%;

  /* Tabular numerals everywhere (parent + detail grids, headers, pinned
     sections, full-width divider rows). font-variant-numeric inherits, so
     a single declaration on the root cascades to every descendant; kitbag
     typography tokens don't set this property, so nothing overrides it. */
  .ag-root-wrapper {
    font-variant-numeric: tabular-nums;
  }

  .ag-root-wrapper,
  .ag-root,
  .ag-cell,
  .ag-group-value {
    ${({ theme }) => theme.typography.bodyMedium};
    color: ${({ theme }) => theme.colours.ink.primary.main};
    font-family: inherit;
  }

  /* Default header text uses .ag-header-cell-text; custom headerComponents
     (e.g. the team-score columns) render inside .ag-header-cell-comp-wrapper
     instead, so apply the heading typography to both so custom headers match
     plain ones. */
  .ag-header-cell-text,
  .ag-header-group-text,
  .ag-header-cell-comp-wrapper {
    ${({ theme }) => theme.typography.headingSmall};
    font-family: inherit;
    text-transform: none;
  }

  /* Vertically center cell contents */
  .ag-cell {
    display: flex;
    align-items: center;
  }

  /* Force kitbag inline padding on cells and headers. AG Grid v34's themes
     emit padding via var(--ag-cell-horizontal-padding), and Uniform's
     dataTable theme sets that var to an undefined Uniform CSS token —
     resolving to 0 padding. Direct property override with !important wins
     over the theme-generated rule and applies in both grids. */
  .ag-cell,
  .ag-header-cell {
    padding-left: ${({ theme }) => theme.sizing.small} !important;
    padding-right: ${({ theme }) => theme.sizing.small} !important;
  }

  /* Right-aligned data cells. AG Grid v34 handles right-aligned text
     headers natively (flex-direction: row-reverse on .ag-header-cell-label),
     but that doesn't reach custom headerComponents — they live inside
     .ag-header-cell-comp-wrapper, which needs an explicit flex-end. */
  .ag-cell.ag-right-aligned-cell {
    justify-content: flex-end;
  }
  .ag-header-cell.ag-right-aligned-header .ag-header-cell-comp-wrapper {
    justify-content: flex-end;
  }

  /* Outer stroke matches Scoring Plays — fully squared, no corner radius.
     Backgrounds: body / rows use canvas.tertiary.main; headers use
     canvas.secondary.main (applies to the nested detail grid too via AG
     Grid's CSS variables). */
  .ag-root-wrapper {
    border: 1px solid ${({ theme }) => theme.colours.canvas.secondary.strong};
    border-radius: 0;
    --ag-background-color: ${({ theme }) =>
      theme.colours.canvas.secondary.main};
    /* AG Grid uses --ag-data-background-color (not --ag-background-color) for
       row and body backgrounds. Without this override, even rows fall back to
       the @hudl/ag-grid theme default (white) in dark mode → visible striping. */
    --ag-data-background-color: ${({ theme }) =>
      theme.colours.canvas.tertiary.main};
    --ag-odd-row-background-color: ${({ theme }) =>
      theme.colours.canvas.tertiary.main};
    --ag-header-background-color: ${({ theme }) =>
      theme.colours.canvas.tertiary.main};
    /* Drive all text/icon colour through kitbag tokens so headers (leaf and
       group), cells, and icons stay legible in both light and dark mode.
       AG Grid v34 uses --ag-header-text-color (not -foreground-color) for
       header text. */
    --ag-foreground-color: ${({ theme }) => theme.colours.ink.primary.main};
    /* @hudl/ag-grid's default theme sets IBM Plex Sans, and AG Grid applies
       it to .ag-row via --ag-cell-font-family, so a cell's
       "font-family: inherit" copies the row's font, not the wrapper's. Set
       all three font variables to the theme font so rows, cells and headers
       use it. */
    --ag-font-family: ${({ theme }) => theme.font.family};
    --ag-cell-font-family: ${({ theme }) => theme.font.family};
    --ag-header-font-family: ${({ theme }) => theme.font.family};
    --ag-header-text-color: ${({ theme }) => theme.colours.ink.primary.strong};
    /* Row hover matches SortableTable and BasicTable; selection uses a tint
       of the interface colour. PlaysGrid sets the same pair. */
    --ag-row-hover-color: ${({ theme }) =>
      theme.colours.canvas.secondary.strong};
    --ag-selected-row-background-color: ${({ theme }) =>
      theme.applyOpacity(theme.colours.interface.main, 'highlight')};
    /* Match kitbag Table cell padding (sizing.small = 8px inline). */
    --ag-cell-horizontal-padding: ${({ theme }) => theme.sizing.small};
    /* Instant master-detail expand/collapse — no animation. */
    --ag-animation-duration: 0s;
    /* Loading overlay — match the row/body background so the preloader
       blends with the table rather than showing a contrasting grey wash. */
    --ag-overlay-background-color: ${({ theme }) =>
      theme.colours.canvas.tertiary.main};
  }

  /* AG Grid v34 renders the loading overlay as a full-width/height wrapper
     that doesn't always honour --ag-overlay-background-color. Force the
     background directly so it matches the row colour in both light and dark. */
  .ag-overlay-loading-wrapper {
    background-color: ${({ theme }) =>
      theme.colours.canvas.tertiary.main} !important;
  }

  /* Floor the grid at header + one data row. Scoped to the DIRECT child of
     AgGridThemedWrap so the master grid gets the floor; the nested detail
     grid's wrapper is deeper in the tree and isn't affected. */
  & > .ag-root-wrapper {
    min-height: calc(var(--ag-header-height) + var(--ag-row-height));
  }

  .ag-row,
  .ag-full-width-row,
  .ag-details-row {
    transition-duration: 0s;
  }

  /* Stretch each row to AT LEAST the full grid width so the bottom divider
     spans past the last column into empty space on the right. Use min-width
     only (not width) so the container/row can still grow past 100% when the
     column total exceeds the viewport — that's what produces the horizontal
     scrollbar. */
  .ag-center-cols-container,
  .ag-row {
    min-width: 100% !important;
  }

  .ag-row:not(:last-child) {
    border-bottom: 1px solid
      ${({ theme }) => theme.colours.canvas.secondary.strong};
  }

  /* Disable row striping. AG Grid v34's new theming API emits
     background-color on .ag-row-even / .ag-row-odd that wins over the CSS
     variables set on .ag-root-wrapper above (--ag-odd-row-background-color
     and --ag-data-background-color). Target the generated classes directly
     so all rows read as a flat list rather than a striped one. */
  .ag-row-even,
  .ag-row-odd {
    background-color: ${({ theme }) =>
      theme.colours.canvas.tertiary.main} !important;
  }

  /* AG Grid v34's .ag-body pulls its background from
     --ag-data-background-color (the row colour). That covers the
     wrapper's --ag-background-color and shows pure white below the last
     row. Force the body to match the quarter divider colour so the empty
     area reads as "non-data" instead of an extra white row. */
  .ag-body {
    background-color: ${({ theme }) =>
      theme.colours.canvas.secondary.main} !important;
  }

  /* Match Scoring Plays' heading row bottom border (kitbag Table uses
     sizing.xtiny solid colours.interface.main on header cells). */
  .ag-header {
    border-bottom: ${({ theme }) => theme.sizing.xtiny} solid
      ${({ theme }) => theme.colours.interface.main};
  }

  /* AG Charts sparkline labels render as SVG <text> nodes. Force them to
     match kitbag bodyMedium (the token applied to .ag-cell above) so they
     read identically to plain-text cells like Clock. */
  .ag-cell svg text {
    fill: ${({ theme }) => theme.colours.ink.primary.main};
    font-family: inherit;
    font-size: ${({ theme }) => theme.typography.bodyMedium.fontSize};
    font-weight: ${({ theme }) => theme.typography.bodyMedium.fontWeight};
    font-variant-numeric: tabular-nums;
  }

  /* Action columns are pinned to the right of both master and detail grids
     so they stay flush with the table edge as more columns are added.
     Single divider on the outer pinned-right container — same 1px line in
     canvas.secondary.strong as the row dividers, so it visually belongs to
     the table. No inner per-column dividers, and no AG Grid box-shadow. */
  .ag-pinned-right-header,
  .ag-pinned-right-cols-container {
    border-left: 1px solid
      ${({ theme }) => theme.colours.canvas.secondary.strong} !important;
    border-right: none !important;
    box-shadow: none !important;
  }
  .ag-pinned-right-header .ag-header-cell,
  .ag-pinned-right-cols-container .ag-cell {
    border-left: none !important;
    border-right: none !important;
    box-shadow: none !important;
  }

  /* Pinned-left section: 1px divider on the outer container (matches the
     pinned-right treatment). Inner cells keep their natural column borders
     so multi-column pinned groups (e.g. checkbox + index) get the same
     vertical dividers as the rest of the grid. */
  .ag-pinned-left-header,
  .ag-pinned-left-cols-container {
    border-right: 1px solid
      ${({ theme }) => theme.colours.canvas.secondary.strong} !important;
    border-left: none !important;
    box-shadow: none !important;
  }
  .ag-cell.action-cell,
  .ag-header-cell.action-cell {
    justify-content: center;
    padding-left: 0;
    padding-right: 0;
  }
  .ag-cell.centered-cell {
    justify-content: center;
  }
  .ag-header-cell.centered-cell .ag-header-cell-comp-wrapper {
    justify-content: center;
  }

  /* Quarter divider full-width rows are headings, not interactive rows —
     no hover state, no pointer cursor, no click handling. Target them by
     the explicit class set via getRowClass rather than every full-width
     row: pointer-events is inherited, so matching the master/detail row
     wrapper would disable every button in the nested play grid. */
  .quarter-divider-row {
    pointer-events: none;
    cursor: default;
  }

  /* Child (detail) grid inset: override AG Grid's default ~30px padding
     with kitbag's 1rem (16px) token, on canvas.secondary.main. */
  .ag-details-row {
    padding: ${({ theme }) => theme.sizing.medium};
    background-color: ${({ theme }) => theme.colours.canvas.secondary.main};
  }

  /* AG Grid floors the auto-height grid's rows section at 150px (per docs:
     https://www.ag-grid.com/react-data-grid/grid-size/#min-height-with-auto-height).
     For the detail grid only, lower it to one row so the actual minimum
     becomes header + one row. ResizeObserver still grows it for taller
     content. Scoped to .ag-details-grid-auto-height so master is untouched. */
  .ag-details-grid-auto-height .ag-center-cols-viewport {
    min-height: var(--ag-row-height) !important;
  }
`;
